import { addressLike } from "@gearbox-protocol/cli-utils";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from "@gearbox-protocol/deploy-tools-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "api";
import type { FC } from "react";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { Address } from "viem";
import { z } from "zod";

const schema = z.object({
  investor: addressLike(),
  token: z.union([z.literal(""), addressLike()]),
});

type RegisterMidasInvestorSchema = z.output<typeof schema>;

interface RegisterMidasInvestorDialogProps {
  executionId: string;
  toggleOpen: (open: boolean) => void;
}

const RegisterMidasInvestorDialog: FC<RegisterMidasInvestorDialogProps> = ({
  executionId,
  toggleOpen,
}) => {
  const { toast } = useToast();

  const form = useForm<RegisterMidasInvestorSchema>({
    resolver: zodResolver(schema) as Resolver<RegisterMidasInvestorSchema>,
    defaultValues: {
      investor: "" as string as Address,
      token: "",
    },
  });

  const submit = form.handleSubmit(async ({ investor, token }) => {
    try {
      const resp = await api.registerMidasInvestor(
        executionId,
        investor,
        token || undefined,
      );
      if (!resp.gateways?.length) {
        toast({
          variant: "destructive",
          title: "Failed to register Midas investor",
          description:
            resp.failed?.map(f => `${f.target}: ${f.error}`).join("; ") ||
            "No Midas gateways were greenlisted",
        });
        return;
      }
      const failedNote = resp.failed?.length
        ? ` Failed: ${resp.failed.map(f => f.target).join(", ")}`
        : "";
      toast({
        variant: "success",
        title: "Registered Midas investor",
        description: `${resp.investor} on ${resp.gateways.length} gateway${resp.gateways.length === 1 ? "" : "s"}.${failedNote}`,
      });
      toggleOpen(false);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to register Midas investor",
        description: `${e}`,
      });
    }
  });

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={true} onOpenChange={toggleOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Register Midas investor</DialogTitle>
          <DialogDescription>
            Greenlist an investor on Midas gateways of this fork
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit} className="space-y-2">
            <FormField
              control={form.control}
              name="investor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Investor</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="0x..."
                      autoComplete="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Wallet or credit account address
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>mToken</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="0x..."
                      autoComplete="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Optional. Leave empty to register on all Midas gateways
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {submitting && (
          <p className="text-muted-foreground text-sm">
            Registering Midas investor, this may take a while...
          </p>
        )}

        <DialogFooter>
          <Button
            type="submit"
            disabled={submitting}
            loading={submitting}
            onClick={submit}
          >
            Register
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegisterMidasInvestorDialog;
